
import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import PillGroup from './PillGroup';
import SceneCard from './SceneCard';
import { apiConstants } from '../../../../utils/apiConstants';
import InputFieldV2 from '../../../../lib/input/InputFieldV2';
import DropDownV2 from '../../../../global/ui/DropDownV2';
import {
    DAY_NIGHT_OPTIONS,
    SCENE_TYPE_OPTIONS,
} from './createScriptUtils';
import { sceneFormData } from '../../../../data/formData';

type SavedScene = {
    id: string;
    itemNumber: number;
    scene: any;
};

const ScenesStep = ({
    fromData,
    stepTwoFormData,
    selectedData,
    activeItem,
    setSelectedData,
    setActiveItem,
}: any) => {
    // Show details always come from Step 1
    const showType = fromData.watch(apiConstants.show_type);
    const isTvShow = showType === 'tv_show';

    const countField = isTvShow ? apiConstants.number_of_episodes : apiConstants.number_of_scenes;

    const selectedCount = fromData.watch(countField);

    const noOfItems = Number(selectedCount ?? selectedCount ?? 0);

    const {
        control,
        handleSubmit,
        reset,
    } = stepTwoFormData;

    const [editingId, setEditingId] = useState<string | null>(null);

    const itemLabel = isTvShow ? 'Episode' : 'Scene';

    // Only entries belonging to the active tab
    const activeScenes = selectedData.filter(
        (item: SavedScene) => item.itemNumber === activeItem
    );

    const resetSceneForm = () => {
        reset(sceneFormData());
        setEditingId(null);
    };

    const handleTabChange = (itemNumber: number) => {
        setActiveItem(itemNumber);
        resetSceneForm();
    };

    // Called only when React Hook Form validation passes
    const submit = (data: any) => {
        const sceneData = { ...data };

        if (editingId) {
            // Update only the selected saved scene
            setSelectedData((prev: SavedScene[]) =>
                prev.map((item) =>
                    item.id === editingId
                        ? {
                            ...item,
                            scene: sceneData,
                        }
                        : item
                )
            );
        } else {
            // Append a new scene under the active episode/scene
            const newScene: SavedScene = {
                id: `${Date.now()}-${Math.random()}`,
                itemNumber: activeItem,
                scene: sceneData,
            };

            setSelectedData((prev: SavedScene[]) => [
                ...prev,
                newScene,
            ]);
        }

        resetSceneForm();
    };

    const handleEdit = (id: string) => {
        const selectedScene = selectedData.find(
            (item: SavedScene) => item.id === id
        );

        if (!selectedScene) return;

        setActiveItem(selectedScene.itemNumber);
        setEditingId(id);

        // Populate all inputs with saved data
        reset(selectedScene.scene);
    };

    const handleDelete = (id: string) => {
        setSelectedData((prev: SavedScene[]) =>
            prev.filter((item) => item.id !== id)
        );

        if (editingId === id) {
            resetSceneForm();
        }
    };

    return (
        <div className="cs-step">
            <h3 className="cs-step__title">
                {isTvShow
                    ? "Let's create scenes for each episode"
                    : "Let's create the different scenes in your show"}
            </h3>

            {/* Dynamic episode / scene tabs */}
            <div className="cs-episode-tabs">
                {Array.from(
                    { length: noOfItems },
                    (_, i) => i + 1
                ).map((itemNumber) => {
                    const count = selectedData.filter(
                        (entry: SavedScene) =>
                            entry.itemNumber === itemNumber
                    ).length;

                    return (
                        <button
                            type="button"
                            key={itemNumber}
                            className={`cs-episode-tab${activeItem === itemNumber
                                ? ' cs-episode-tab--active'
                                : ''
                                }`}
                            onClick={() => handleTabChange(itemNumber)}
                        >
                            {itemLabel} {itemNumber}
                            {count > 0 ? ` (${count})` : ''}
                        </button>
                    );
                })}
            </div>

            {/* Saved entries for selected tab */}
            {activeScenes.length > 0 && (
                <div className="cs-scene-list">
                    {activeScenes.map((item: SavedScene) => (
                        <SceneCard
                            key={item.id}
                            scene={item.scene}
                            onEdit={() => handleEdit(item.id)}
                            onDelete={() => handleDelete(item.id)}
                        />
                    ))}
                </div>
            )}

            <div className="cl-step__fields">
                <h3 className="cs-step__title">
                    {editingId
                        ? 'Edit Scene'
                        : `Add Scene - ${itemLabel} ${activeItem}`}
                </h3>

                <InputFieldV2
                    name={apiConstants.set_name}
                    label="Set name"
                    placeholder="Enter name"
                    rules={{
                        required: 'Set name is required',
                        validate: (value: string) =>
                            !!value?.trim() ||
                            'Set name cannot be empty',
                    }}
                />

                <InputFieldV2
                    name={apiConstants.scene_number}
                    label="Scene number"
                    placeholder="Enter scene number"
                    rules={{
                        required: 'Scene number is required',
                        pattern: {
                            value: /^[0-9]+$/,
                            message: 'Only numbers are allowed',
                        },
                    }}
                />

                <InputFieldV2
                    name={apiConstants.pages}
                    label="Pages -/8"
                    placeholder="Enter to closest eighth"
                    rules={{
                        required: 'Pages are required',
                        pattern: {
                            value: /^(?:\d+|[1-7]\/8|\d+\s+[1-7]\/8)$/,
                            message:
                                'Enter valid pages (e.g. 1, 3/8, 2 3/8)',
                        },
                    }}
                />

                <Controller
                    name={apiConstants.day_or_night}
                    control={control}
                    rules={{
                        validate: (value) =>
                            (value != null &&
                                value !== '' &&
                                (typeof value !== 'object' ||
                                    (value.value != null &&
                                        value.value !== ''))) ||
                            'Day or night is required',
                    }}
                    render={({ field, fieldState }) => (
                        <DropDownV2
                            name={apiConstants.day_or_night}
                            label="Day or night"
                            placeholder="Select"
                            data={DAY_NIGHT_OPTIONS}
                            selectedItems={field.value}
                            errorMessage={fieldState.error?.message || ''}
                            onSelect={field.onChange}
                        />
                    )}
                />

                <h3 className="cs-step__title">
                    Is this scene a location or set?
                </h3>

                <SceneTypeField />

                <div className="cs-scene-actions">
                    <button
                        type="button"
                        className="cs-link-btn"
                        onClick={handleSubmit(submit)}
                    >
                        {editingId ? 'Update Scene' : 'Save & Add'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const SceneTypeField = () => {
    const { control } = useFormContext();

    return (
        <Controller
            name={apiConstants.scene_type}
            control={control}
            rules={{
                validate: (value) =>
                    (value != null &&
                        value !== '' &&
                        (typeof value !== 'object' ||
                            (value.value != null &&
                                value.value !== ''))) ||
                    'Please select Location or Set',
            }}
            render={({ field, fieldState }) => (
                <>
                    <PillGroup
                        name={apiConstants.scene_type}
                        options={SCENE_TYPE_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                    />

                    {fieldState.error && (
                        <p className="error-message" role="alert">
                            {fieldState.error.message}
                        </p>
                    )}
                </>
            )}
        />
    );
};

export default ScenesStep;